import { Badge, Button, Input, LoadingSpinner, Text } from "@/components/ui";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { phaseActionDefinitionsQueryOptions } from "@/lib/data/react-query/matters/matter-phase";
import { useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Info, Search } from "lucide-react";
import { useEffect, useState } from "react";
import {
  PHASE_WIDGET_CATALOG,
  createPhaseInformationWidget,
} from "@/lib/data/static/phase-widget-catalog";
import { ManualActionEditorModal } from "./ManualActionEditorModal";
import { PhaseWidgetPreview } from "./PhaseWidgetPreview";
import { useUser } from "@/components/UserProvider";
import { MATTER_PHASE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { Plus } from "lucide-react";

const PAGE_SIZE = 12;

export function ActionRegistryView() {
  const { hasGrant } = useUser();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250).trim();
  const [pageNumber, setPageNumber] = useState(1);
  const { data, isLoading, isFetching, isError } = useQuery(
    phaseActionDefinitionsQueryOptions({
      q: debouncedSearch || undefined,
      pageNumber,
      pageSize: PAGE_SIZE,
    }),
  );

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  const count = data?.pagination.count ?? 0;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const firstVisibleAction = (pageNumber - 1) * PAGE_SIZE + 1;
  const lastVisibleAction = Math.min(pageNumber * PAGE_SIZE, count);
  const widgets = PHASE_WIDGET_CATALOG.filter((widget) =>
    `${widget.title} ${widget.description}`
      .toLowerCase()
      .includes(debouncedSearch.toLowerCase()),
  );

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <Text variant="cardTitle">Actions and widgets</Text>
        </div>
        {hasGrant(MATTER_PHASE_PERMISSION_KEYS.phaseProject, "create") ? (
          <Button
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Create checkbox action
          </Button>
        ) : null}
      </div>

      <div className="border-border/60 bg-muted/30 rounded-lg border p-4">
        <div className="relative max-w-2xl">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            className="bg-background pl-9"
            placeholder="Search actions and widgets"
          />
        </div>
      </div>

      {widgets.length > 0 ? (
        <section
          aria-label="Informational widgets"
          className="@container space-y-3"
        >
          <div className="flex items-center justify-between gap-3">
            <Text variant="body">Informational widgets</Text>
            <Badge
              variant="outline"
              className="text-muted-foreground font-normal"
            >
              Sample data
            </Badge>
          </div>
          <div className="grid grid-cols-1 items-stretch gap-4 @min-[760px]:grid-cols-2">
            {widgets.map((widget) => (
              <div
                key={widget.type}
                className={widget.wide ? "@min-[760px]:col-span-full" : ""}
                title={widget.description}
              >
                <PhaseWidgetPreview
                  widget={createPhaseInformationWidget(widget.type)}
                  actions={[]}
                />
              </div>
            ))}
          </div>
        </section>
      ) : null}
      <section
        aria-label="Actions"
        className="border-border/60 @container overflow-hidden rounded-xl border"
      >
        <div className="border-border/60 bg-muted/30 flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
          <div>
            <Text variant="body">Actions</Text>
            {count > 0 ? (
              <Text variant="caption">
                {firstVisibleAction}–{lastVisibleAction} of {count} Actions
              </Text>
            ) : null}
          </div>
          {pages > 1 ? (
            <nav
              aria-label="Actions pagination"
              className="flex items-center gap-2"
            >
              <Text variant="caption" className="mr-1">
                Page {pageNumber} of {pages}
              </Text>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                aria-label="Previous actions page"
                disabled={pageNumber === 1}
                onClick={() => {
                  setPageNumber((page) => page - 1);
                }}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="h-8 w-8"
                aria-label="Next actions page"
                disabled={pageNumber >= pages}
                onClick={() => {
                  setPageNumber((page) => page + 1);
                }}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </nav>
          ) : null}
        </div>

        <div className="relative p-4">
          {isFetching && !isLoading ? (
            <div className="bg-background/50 absolute inset-0 z-10 flex items-center justify-center backdrop-blur-[1px]">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : null}
          {isLoading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : isError ? (
            <Text variant="subHeading">
              Action definitions could not be loaded. Please try again.
            </Text>
          ) : data?.data.length ? (
            <div className="grid min-w-0 gap-4 @min-[760px]:grid-cols-2">
              {data.data.map((action) => (
                <section
                  key={action.id}
                  aria-label={`Preview of ${action.title}`}
                  className="bg-card flex min-h-44 min-w-0 flex-col justify-between gap-3 rounded-2xl border p-5"
                >
                  <div className="flex items-start gap-2">
                    <p className="text-sm font-medium break-words">
                      {action.title}
                    </p>
                    {action.description ? (
                      <span
                        title={action.description}
                        aria-label={action.description}
                      >
                        <Info
                          className="text-muted-foreground size-4 shrink-0"
                          aria-hidden="true"
                        />
                      </span>
                    ) : null}
                  </div>
                  <Badge
                    variant="outline"
                    className="text-muted-foreground w-fit font-normal"
                  >
                    Incomplete
                  </Badge>
                </section>
              ))}
            </div>
          ) : (
            <div className="py-10 text-center">
              <Text variant="body">No actions found</Text>
              <Text variant="caption">Try a different search term.</Text>
            </div>
          )}
        </div>
      </section>
      <ManualActionEditorModal open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}
